import { useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Path, useFormContext, UseFormReturn } from 'react-hook-form';
import { DictionaryMdService } from '@spilca/dictionary_ts/v1/dictionary_web/dictionaries_md_pb_service';
import { Product } from '@spilca/order_ts/v1/order/model_product_pb';
import {
  ListProductRequest,
  ListProductResponse,
  ProductFilter
} from '@spilca/dictionary_ts/v1/dictionary_md/product_pb';
import { string } from '@monorepo/helpers';
import { LookUpField, TFormField } from '@monorepo/forms';
import { getGrpcPromise, TGRPCPromiseReject } from '@monorepo/grpc';

type TProductField<TFormValues> = {
  valueKey: Path<TFormValues>;
  product: Product.AsObject;
  typeId?: string | number;
} & TFormField<TFormValues>;

/**
 * Извлекает первую цифру из typeId для определения process
 * @param typeId - ID типа продукта (например, '100', '101', '200')
 * @returns первая цифра typeId или 0 если некорректный typeId
 */
const getProcessFromTypeId = (typeId: string | number): number => {
  const typeIdStr = String(typeId);
  const firstDigit = typeIdStr.charAt(0);
  const process = parseInt(firstDigit, 10);
  return isNaN(process) ? 0 : process;
};

const ProductField = <
  TFormValues extends Record<string, any> = Record<string, any>
>({
  valueKey,
  product,
  name,
  typeId,
  ...props
}: TProductField<TFormValues>) => {
  const { setValue }: UseFormReturn<TFormValues> =
    useFormContext<TFormValues>();

  // Безопасное получение typeId с проверками
  const normalizedTypeId = typeId !== undefined ? String(typeId) : '';
  const typeIdNumber = normalizedTypeId ? parseInt(normalizedTypeId, 10) : 0;
  const processNumber = getProcessFromTypeId(normalizedTypeId);

  const handleSelect = (data: any) => {
    setValue(valueKey, data);
    setValue('brandName' as any, data.brandName);
    setValue('categoryName' as any, data.categoriesList[0]?.name || '');
    setValue('productGroupName' as any, data.productGroupName);
  };

  const searchMutator = (
    query: string
  ): Promise<ListProductResponse.AsObject> => {
    const request = new ListProductRequest();
    const filter = new ProductFilter();

    if (typeIdNumber > 0) {
      filter.setType(typeIdNumber as any);
    }

    if (processNumber > 0) {
      filter.setProcess(processNumber as any);
    }

    filter.setName(string(query));
    request.setFilter(filter);

    return getGrpcPromise(DictionaryMdService.ListProduct)(request);
  };

  const { mutate, isLoading, data } = useMutation<
    ListProductResponse.AsObject,
    TGRPCPromiseReject,
    string
  >({ mutationFn: searchMutator, retry: false });

  const items = useMemo(
    () =>
      data?.itemsList?.length
        ? data.itemsList.map((item) => ({
            ...item.product,
            subTitle: item.product?.brandName,
            quantityTypesList: item.quantityTypesList,
            categoriesList: item.categoriesList
          }))
        : [],
    [data]
  );

  return (
    <LookUpField
      searchFc={mutate}
      isLoading={isLoading}
      items={items}
      onSelect={handleSelect}
      name={name}
      {...props}
    />
  );
};

export default ProductField;
